import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, Search, AlertCircle, Loader, Trash2 } from 'lucide-react';
import { supabase, adminSupabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

type User = {
  id: string;
  username: string;
  role: 'reader' | 'author' | 'admin';
  created_at: string;
  full_name: string | null;
  last_sign_in_at: string | null;
};

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/auth', { state: { returnTo: '/admin' } });
      return;
    }

    checkAdminAccess();
  }, [user, authLoading]);

  const checkAdminAccess = async () => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user!.id)
        .single();

      if (profileError) throw profileError;

      if (profile?.role !== 'admin') {
        navigate('/');
        return;
      }

      setIsAdmin(true);
      fetchUsers();
    } catch (err) {
      console.error('Error checking admin access:', err);
      navigate('/');
    }
  };

  const fetchUsers = async () => {
    try {
      if (!adminSupabase) {
        throw new Error('Admin access not configured');
      }

      // Get all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      // Get auth metadata using admin client
      const { data: { users: authUsers }, error: authError } = await adminSupabase.auth.admin.listUsers();

      if (authError) throw authError;

      // Combine the data
      const usersWithAuth = profiles.map(profile => ({
        ...profile,
        last_sign_in_at: authUsers.find(auth => auth.id === profile.id)?.last_sign_in_at || null
      }));

      setUsers(usersWithAuth);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId: string, newRole: 'reader' | 'author' | 'admin') => {
    try {
      setUpdatingUserId(userId);
      setError(null);

      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) throw error;

      // Log admin action
      await supabase.from('admin_logs').insert({
        admin_id: user!.id,
        action: 'update_role',
        target_user_id: userId,
        details: { new_role: newRole }
      });

      await fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user role');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const deleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingUserId(userId);
      setError(null);

      // Log the deletion attempt
      await supabase.from('admin_logs').insert({
        admin_id: user!.id,
        action: 'delete_user',
        target_user_id: userId,
        details: { status: 'initiated' }
      });

      // Delete the user using the RPC function
      const { error: deleteError } = await supabase.rpc('admin_delete_user', {
        target_user_id: userId
      });

      if (deleteError) throw deleteError;

      // Log successful deletion
      await supabase.from('admin_logs').insert({
        admin_id: user!.id,
        action: 'delete_user',
        target_user_id: userId,
        details: { status: 'completed' }
      });

      await fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
      
      // Log failed deletion
      await supabase.from('admin_logs').insert({
        admin_id: user!.id,
        action: 'delete_user',
        target_user_id: userId,
        details: { 
          status: 'failed',
          error: err instanceof Error ? err.message : 'Unknown error'
        }
      });
    } finally {
      setDeletingUserId(null);
    }
  };

  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (user.full_name && user.full_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (authLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="max-w-6xl mx-auto px-4">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-indigo-600" />
            Admin Panel
          </h1>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">User</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Role</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Joined</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Last Login</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.map((userData) => (
                  <tr key={userData.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <Link 
                          to={`/profile/${userData.id}`}
                          className="font-medium text-indigo-600 hover:text-indigo-800"
                        >
                          {userData.username}
                        </Link>
                        {userData.full_name && (
                          <div className="text-sm text-gray-500">{userData.full_name}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={userData.role}
                        onChange={(e) => updateUserRole(userData.id, e.target.value as 'reader' | 'author' | 'admin')}
                        disabled={updatingUserId === userData.id || userData.id === user?.id}
                        className={`px-3 py-1.5 text-sm font-medium rounded-lg border ${
                          userData.role === 'admin' 
                            ? 'bg-purple-50 text-purple-700 border-purple-200'
                            : userData.role === 'author'
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : 'bg-gray-50 text-gray-700 border-gray-200'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <option value="reader">Reader</option>
                        <option value="author">Author</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(userData.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {userData.last_sign_in_at 
                        ? new Date(userData.last_sign_in_at).toLocaleString()
                        : 'Never'}
                    </td>
                    <td className="px-4 py-3">
                      {userData.id !== user?.id && (
                        <button
                          onClick={() => deleteUser(userData.id)}
                          disabled={deletingUserId === userData.id}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                          title="Delete user"
                        >
                          {deletingUserId === userData.id ? (
                            <Loader className="w-5 h-5 animate-spin" />
                          ) : (
                            <Trash2 className="w-5 h-5" />
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}